import axios from "axios";
import { BLOG_POSTS, JOBS, demoTracking } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// STATIC build mode: no backend. Enabled at build time via REACT_APP_STATIC=true
export const IS_STATIC = process.env.REACT_APP_STATIC === "true";
const COMPANY_EMAIL = "info@agarwalsecurepackers.com";

export const TOKEN_KEY = "asp_admin_token";

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

function mailtoSubmit(subject, data, humanLabels = {}) {
  const lines = Object.entries(data)
    .filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== "")
    .map(([k, v]) => `${humanLabels[k] || k}: ${v}`)
    .join("\n");
  const href = `mailto:${COMPANY_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines)}`;
  try {
    window.location.href = href;
  } catch (e) {
    /* noop */
  }
  return { status: "success", message: "Opening your email app to send us the details…" };
}

// ---------------- Public ----------------
export const submitQuote = async (data) => {
  if (IS_STATIC) {
    await delay(300);
    return mailtoSubmit(`Quote Request — ${data.name}`, data, {
      name: "Name", phone: "Phone", email: "Email", pickup_city: "Pickup City",
      drop_city: "Drop City", move_date: "Move Date", move_type: "Type of Move", message: "Message",
    });
  }
  return api.post("/quote", data).then((r) => r.data);
};

export const submitContact = async (data) => {
  if (IS_STATIC) {
    await delay(300);
    return mailtoSubmit(`Contact — ${data.name}`, data, {
      name: "Name", email: "Email", phone: "Phone", subject: "Subject", message: "Message",
    });
  }
  return api.post("/contact", data).then((r) => r.data);
};

export const applyJob = async (data) => {
  if (IS_STATIC) {
    await delay(300);
    return mailtoSubmit(`Job Application — ${data.position} — ${data.name}`, data, {
      name: "Name", email: "Email", phone: "Phone", position: "Position",
      experience: "Experience", cover_letter: "Cover Letter",
    });
  }
  return api.post("/careers/apply", data).then((r) => r.data);
};

export const getBlog = async () => {
  if (IS_STATIC) return [...BLOG_POSTS];
  return api.get("/blog").then((r) => r.data);
};

export const getBlogPost = async (slug) => {
  if (IS_STATIC) {
    const post = BLOG_POSTS.find((p) => p.slug === slug);
    if (!post) return Promise.reject({ response: { data: { detail: "Post not found" } } });
    return post;
  }
  return api.get(`/blog/${slug}`).then((r) => r.data);
};

export const getJobs = async () => {
  if (IS_STATIC) return [...JOBS];
  return api.get("/careers/jobs").then((r) => r.data);
};

export const trackShipment = async (id) => {
  if (IS_STATIC) {
    await delay(400);
    if (!id || id.trim().length < 4) {
      return Promise.reject({ response: { data: { detail: "Please enter a valid booking ID." } } });
    }
    return demoTracking(id);
  }
  return api.get(`/track/${encodeURIComponent(id)}`).then((r) => r.data);
};

// ---------------- Admin (backend only) ----------------
export const adminLogin = (data) => api.post("/auth/login", data).then((r) => r.data);
export const adminMe = () => api.get("/auth/me").then((r) => r.data);
export const adminListBookings = () => api.get("/admin/bookings").then((r) => r.data);
export const adminCreateBooking = (data) => api.post("/admin/bookings", data).then((r) => r.data);
export const adminGetBooking = (id) => api.get(`/admin/bookings/${encodeURIComponent(id)}`).then((r) => r.data);
export const adminUpdateStatus = (id, data) =>
  api.patch(`/admin/bookings/${encodeURIComponent(id)}/status`, data).then((r) => r.data);
export const adminListQuotes = () => api.get("/admin/quotes").then((r) => r.data);
export const adminUpdateQuoteStatus = (id, data) =>
  api.patch(`/admin/quotes/${encodeURIComponent(id)}/status`, data).then((r) => r.data);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
